import { useState, useRef, useEffect } from "react";
import { Folder, FolderPlus, MoreVertical, Pencil, Palette, Trash2, ListFilter } from "lucide-react";

const PRESET_COLORS = [
  "#666666", // grey (default)
  "#4a90e2", // blue
  "#22c55e", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#a855f7", // purple
  "#f97316", // orange
  "#14b8a6", // teal
];

/**
 * Sidebar for library filtering by folder.
 *
 * Props:
 *   folders          – [{id, name, color, video_count, sort_order}]
 *   selectedFolder   – "all" | "none" | folderId
 *   totalCount       – integer, videos across all folders (for "Alle Videos")
 *   uncategorizedCount – integer
 *   onSelect(folderKey)
 *   onCreateFolder(name, color) -> Promise
 *   onRenameFolder(id, name)    -> Promise
 *   onRecolorFolder(id, color)  -> Promise
 *   onDeleteFolder(id)          -> Promise
 */
export default function FolderSidebar({
  folders,
  selectedFolder,
  totalCount,
  uncategorizedCount,
  onSelect,
  onCreateFolder,
  onRenameFolder,
  onRecolorFolder,
  onDeleteFolder,
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[1]);
  const [menuOpenFor, setMenuOpenFor] = useState(null); // folder id
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [colorPickerFor, setColorPickerFor] = useState(null); // folder id
  const [deleteConfirmFor, setDeleteConfirmFor] = useState(null); // folder
  const menuRef = useRef(null);
  const renameInputRef = useRef(null);
  const createInputRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenFor(null);
      }
    };
    if (menuOpenFor) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpenFor]);

  useEffect(() => {
    if (creating) createInputRef.current?.focus();
  }, [creating]);

  useEffect(() => {
    if (renamingId) renameInputRef.current?.focus();
  }, [renamingId]);

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    const n = newName.trim();
    if (!n) return;
    await onCreateFolder(n, newColor);
    setNewName("");
    setNewColor(PRESET_COLORS[1]);
    setCreating(false);
  };

  const submitRename = async (id) => {
    const n = renameValue.trim();
    if (!n) return;
    await onRenameFolder(id, n);
    setRenamingId(null);
    setRenameValue("");
  };

  return (
    <aside
      className="w-full lg:w-[220px] lg:shrink-0 space-y-1"
      data-testid="folder-sidebar"
    >
      <SidebarItem
        icon={<ListFilter size={14} />}
        label="Alle Videos"
        count={totalCount}
        active={selectedFolder === "all"}
        onClick={() => onSelect("all")}
        testId="folder-sidebar-all"
      />
      <SidebarItem
        icon={<Folder size={14} />}
        label="Ohne Ordner"
        count={uncategorizedCount}
        active={selectedFolder === "none"}
        onClick={() => onSelect("none")}
        testId="folder-sidebar-uncategorized"
      />

      <div className="pt-3 border-t border-[#2a2a30] my-2" />

      {folders.map((f) => {
        const isSelected = selectedFolder === f.id;
        const isRenaming = renamingId === f.id;
        const showColorPicker = colorPickerFor === f.id;
        return (
          <div key={f.id} className="relative">
            {isRenaming ? (
              <form
                className="flex items-center gap-1 px-2 h-9 rounded-md border border-[#3a3a40] bg-[#1f1f23]"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitRename(f.id);
                }}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: f.color }}
                />
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => submitRename(f.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setRenamingId(null);
                      setRenameValue("");
                    }
                  }}
                  maxLength={60}
                  className="flex-1 bg-transparent text-sm text-white outline-none min-w-0"
                  data-testid={`folder-sidebar-rename-input-${f.id}`}
                />
              </form>
            ) : (
              <div
                className={`group flex items-center gap-2 pl-2 pr-1 h-9 rounded-md cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-[#1f1f23] text-white"
                    : "text-[#c8c8d0] hover:bg-[#17171a]"
                }`}
                data-testid={`folder-sidebar-item-${f.id}`}
                onClick={() => onSelect(f.id)}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: f.color }}
                />
                <span className="text-sm truncate flex-1" title={f.name}>{f.name}</span>
                <span className="text-[10px] text-[#5a5a60] tabular-nums shrink-0">
                  {f.video_count}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenFor(menuOpenFor === f.id ? null : f.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center justify-center w-6 h-6 rounded text-[#9a9aa3] hover:text-white hover:bg-[#2a2a30]"
                  data-testid={`folder-sidebar-menu-${f.id}`}
                  aria-label={`Aktionen für ${f.name}`}
                >
                  <MoreVertical size={14} />
                </button>
              </div>
            )}

            {menuOpenFor === f.id && (
              <div
                ref={menuRef}
                className="absolute right-0 top-9 z-30 w-44 rounded-lg border border-[#2a2a30] bg-[#17171a] shadow-lg py-1"
                data-testid={`folder-sidebar-menu-panel-${f.id}`}
              >
                <button
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-[#c8c8d0] hover:bg-[#1f1f23]"
                  onClick={() => {
                    setRenamingId(f.id);
                    setRenameValue(f.name);
                    setMenuOpenFor(null);
                  }}
                  data-testid={`folder-sidebar-rename-${f.id}`}
                >
                  <Pencil size={12} /> Umbenennen
                </button>
                <button
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-[#c8c8d0] hover:bg-[#1f1f23]"
                  onClick={() => {
                    setColorPickerFor(f.id);
                    setMenuOpenFor(null);
                  }}
                  data-testid={`folder-sidebar-color-${f.id}`}
                >
                  <Palette size={12} /> Farbe ändern
                </button>
                <button
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10"
                  onClick={() => {
                    setDeleteConfirmFor(f);
                    setMenuOpenFor(null);
                  }}
                  data-testid={`folder-sidebar-delete-${f.id}`}
                >
                  <Trash2 size={12} /> Löschen
                </button>
              </div>
            )}

            {showColorPicker && (
              <div
                className="absolute left-0 right-0 top-10 z-20 rounded-lg border border-[#2a2a30] bg-[#17171a] p-2 shadow-lg"
                data-testid={`folder-sidebar-color-panel-${f.id}`}
              >
                <div className="grid grid-cols-4 gap-2">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={async () => {
                        await onRecolorFolder(f.id, c);
                        setColorPickerFor(null);
                      }}
                      className="w-8 h-8 rounded-md border border-[#2a2a30] hover:scale-110 transition-transform"
                      style={{ backgroundColor: c }}
                      aria-label={`Farbe ${c}`}
                      data-testid={`folder-sidebar-color-swatch-${c.slice(1)}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-2 w-full text-[11px] text-[#9a9aa3] hover:text-white"
                  onClick={() => setColorPickerFor(null)}
                >
                  Schließen
                </button>
              </div>
            )}

            {deleteConfirmFor?.id === f.id && (
              <div
                className="absolute left-0 right-0 top-10 z-20 rounded-lg border border-red-500/40 bg-[#17171a] p-3 shadow-lg"
                data-testid={`folder-sidebar-delete-confirm-${f.id}`}
              >
                <p className="text-xs text-[#c8c8d0] leading-snug">
                  „{f.name}&quot; löschen? {f.video_count > 0 ? (
                    <span>
                      {f.video_count === 1 ? (
                        <>Das enthaltene Video wird nach „Ohne Ordner&quot; verschoben.</>
                      ) : (
                        <>Die {f.video_count} enthaltenen Videos werden nach „Ohne Ordner&quot; verschoben.</>
                      )}
                    </span>
                  ) : (
                    <span>Der Ordner ist leer.</span>
                  )}
                </p>
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmFor(null)}
                    className="text-[11px] px-2 py-1 rounded text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23]"
                    data-testid={`folder-sidebar-delete-cancel-${f.id}`}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await onDeleteFolder(f.id);
                      setDeleteConfirmFor(null);
                    }}
                    className="text-[11px] px-2 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30"
                    data-testid={`folder-sidebar-delete-confirm-btn-${f.id}`}
                  >
                    Endgültig löschen
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="pt-1">
        {creating ? (
          <form
            onSubmit={submitCreate}
            className="border border-[#3a3a40] bg-[#17171a] rounded-md p-2 space-y-2"
            data-testid="folder-sidebar-create-form"
          >
            <input
              ref={createInputRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setCreating(false);
                  setNewName("");
                }
              }}
              maxLength={60}
              placeholder="Ordnername"
              className="w-full h-8 px-2 rounded bg-[#0a0a0b] text-sm text-white outline-none border border-[#2a2a30] focus:border-[#ff6b35]"
              data-testid="folder-sidebar-create-input"
            />
            <div className="flex flex-wrap gap-1">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={`w-5 h-5 rounded-sm border ${
                    newColor === c ? "border-white ring-1 ring-white/40" : "border-[#2a2a30]"
                  }`}
                  style={{ backgroundColor: c }}
                  aria-label={`Farbe ${c}`}
                />
              ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewName("");
                }}
                className="text-[11px] px-2 py-1 rounded text-[#9a9aa3] hover:text-white"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={!newName.trim()}
                className="text-[11px] px-2 py-1 rounded bg-[#ff6b35] text-white font-medium disabled:opacity-50"
                data-testid="folder-sidebar-create-submit"
              >
                Erstellen
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="w-full flex items-center gap-2 px-2 h-9 rounded-md text-sm text-[#9a9aa3] hover:text-white hover:bg-[#17171a] transition-colors border border-dashed border-[#2a2a30]"
            data-testid="folder-sidebar-create-btn"
          >
            <FolderPlus size={14} /> Neuer Ordner
          </button>
        )}
      </div>
    </aside>
  );
}

function SidebarItem({ icon, label, count, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2 h-9 rounded-md text-sm transition-colors ${
        active
          ? "bg-[#1f1f23] text-white"
          : "text-[#c8c8d0] hover:bg-[#17171a]"
      }`}
      data-testid={testId}
    >
      {icon}
      <span className="flex-1 text-left truncate">{label}</span>
      <span className="text-[10px] text-[#5a5a60] tabular-nums">{count}</span>
    </button>
  );
}
